import { EditOutlined } from "@ant-design/icons";
import {
  Card,
  DatePicker,
  Form,
  Input,
  Popconfirm,
  Select,
  Switch,
  Table,
} from "antd";
import type { FormInstance } from "antd/es/form";
import type { InputRef } from "antd/es/input";
import moment from "moment";
import React from "react";
import { useNotification } from "../../contexts/Notifications";
import DivisionSelector from "./DivisionSelector";
import { EDIT_BATCHES_TXT, EditBatchesButton } from "./EditBatchesButton";
import { EditCustomerButton } from "./EditCustomerButton";
import {
  EDIT_WORKING_DAYS_TXT,
  EditWorkingDaysButton,
} from "./EditWorkingDaysButton";
import { NewProjectButton } from "./NewProjectButton";
import { Project } from "./ProjectsTableContainer";
import styles from "./styles.module.css";

type TableData = {
  startDate: Date | null;
  endDate: Date | null;
  customer: string;
  project: string;
  displayName: string | undefined;
  defaultLanguage: string;
  key: string;
  isCompleted: boolean | undefined;
  isTracking: boolean | undefined;
  isTicksheetProject: boolean | undefined;
  division: string | undefined;
  workingDays?: number[] | null;
};

type ColumnType = {
  title: string | React.ReactNode;
  dataIndex: string;
  key: string;
  width?: number;
  editable?: boolean;
  isRadio?: boolean;
  isDate?: boolean;
  fixed?: "left" | "right";
  render?: (value: any, data: TableData) => React.ReactNode;
};

const ColumnTitle = (title: string) => (
  <div className={styles["table-editable-cell"]}>
    {title} <EditOutlined className={styles["table-editable-cell-icon"]} />
  </div>
);
const COLUMNS: ColumnType[] = [
  {
    title: "Customer",
    dataIndex: "customer",
    key: "customer",
  },
  {
    title: "Project",
    dataIndex: "project",
    key: "project",
  },
  {
    title: ColumnTitle("Display Name"),
    dataIndex: "displayName",
    key: "displayName",
    editable: true,
  },
  {
    title: "Division",
    dataIndex: "division",
    key: "division",
  },
  {
    title: "Live",
    dataIndex: "isTracking",
    key: "isTracking",
    isRadio: true,
  },
  {
    title: "Completed",
    dataIndex: "isCompleted",
    key: "isCompleted",
    isRadio: true,
  },
  {
    title: "Tick Sheet",
    dataIndex: "isTicksheetProject",
    key: "isTicksheetProject",
    isRadio: true,
  },
  {
    title: "Default Language",
    dataIndex: "defaultLanguage",
    key: "defaultLanguage",
  },
  {
    title: "Planned Start Date",
    dataIndex: "startDate",
    key: "startDate",
    editable: true,
    isDate: true,
  },
  {
    title: "Planned End Date",
    dataIndex: "endDate",
    key: "endDate",
    isDate: true,
  },
  {
    title: "Plan received on",
    dataIndex: "receivedOn",
    key: "receivedOn",
    isDate: true,
  },
  {
    title: EDIT_BATCHES_TXT,
    dataIndex: "batches",
    key: "batches",
    render: (_, data: TableData) => (
      <EditBatchesButton customer={data.customer} project={data.project} />
    ),
  },
  {
    title: EDIT_WORKING_DAYS_TXT,
    dataIndex: "workingDays",
    key: "workingDays",
    render: (_, data: TableData) => (
      <EditWorkingDaysButton
        customer={data.customer}
        project={data.project}
        workingDays={data.workingDays}
      />
    ),
  },
];

const EditableContext = React.createContext<FormInstance<any> | null>(null);

interface Item {
  key: string;
  customer: string;
  project: string;
  displayName: string;
  defaultLanguage: string;
  startDate: Date;
  endDate: Date;
}

const EditableRow = (props) => {
  const [form] = Form.useForm();
  return (
    <Form form={form} component={false}>
      <EditableContext.Provider value={form}>
        <tr {...props} />
      </EditableContext.Provider>
    </Form>
  );
};

interface EditableCellProps {
  title: React.ReactNode;
  editable: boolean;
  children: React.ReactNode;
  dataIndex: keyof Item;
  record: Item;
  handleSave: (record: Item, originalRecord?: Item) => void;
}

const EditableCell: React.FC<EditableCellProps> = (
  props: EditableCellProps,
) => {
  const { title, editable, children, dataIndex, record, handleSave } = props;
  const notify = useNotification();
  const [editing, setEditing] = React.useState(false);
  const [showConfirm, setShowConfirm] = React.useState(false);
  const inputRef = React.useRef<InputRef>(null);
  const form = React.useContext(EditableContext)!;

  React.useEffect(() => {
    if (editing) {
      inputRef.current!.focus();
    }
  }, [editing]);

  const toggleEdit = () => {
    setEditing(!editing);
    form.setFieldsValue({ [dataIndex]: record[dataIndex] });
  };

  const save = async () => {
    try {
      const values = await form.validateFields();

      setShowConfirm(false);
      toggleEdit();
      handleSave({ ...record, ...values }, record);
    } catch (errInfo) {
      notify(`Failed to save changes to ${title}`, "error");
    }
  };

  const requestConfirm = async () => {
    if (showConfirm) {
      return;
    }
    try {
      await form.validateFields([dataIndex]);
      const value = form.getFieldValue(dataIndex);
      if (value === record[dataIndex]) {
        toggleEdit();
        return;
      }
      setShowConfirm(true);
    } catch (errInfo) {
      notify(`Failed to validate ${title}`, "error");
    }
  };

  let childNode = children;

  if (editable) {
    childNode = editing ? (
      <Popconfirm
        open={showConfirm}
        title={`Are you sure want to save changes to ${record.displayName}？`}
        okText="Yes"
        cancelText="No"
        onConfirm={save}
        onCancel={() => {
          setShowConfirm(false);
          toggleEdit();
        }}
        destroyTooltipOnHide={false}
      >
        <span className={styles["editable-field-wrapper"]}>
          <Form.Item
            style={{ margin: 0 }}
            name={dataIndex}
            rules={[
              {
                required: true,
                message: `Project display name cannot be empty`,
              },
              {
                validator: (_, value) => {
                  if (value && value.trim() === "") {
                    return Promise.reject(
                      new Error(`Project display name cannot be empty`),
                    );
                  }
                  return Promise.resolve();
                },
              },
            ]}
          >
            <Input
              ref={inputRef}
              onPressEnter={requestConfirm}
              onBlur={requestConfirm}
            />
          </Form.Item>
        </span>
      </Popconfirm>
    ) : (
      <div onClick={toggleEdit}>{children}</div>
    );
  }
  return <td>{childNode}</td>;
};

type ProjectsTableProps = {
  projects: Project[];
  onCellValueChange: (project: Project, changedField: string) => Promise<any>;
};

interface TableRow {
  key: string;
  customer: string;
  project: string;
  displayName: string | undefined;
  defaultLanguage: string;
  startDate: Date | string | undefined;
  endDate: Date | string | undefined;
  isCompleted: boolean | undefined;
  isTracking: boolean | undefined;
  isTicksheetProject: boolean | undefined;
  division: string | undefined;
  receivedOn: Date | string | undefined;
}

export const ProjectsTable = (props: ProjectsTableProps) => {
  const { onCellValueChange, projects } = props;
  const [selectedCustomer, setSelectedCustomer] = React.useState<string>("All");

  const handleCustomerChange = (value: string) => {
    setSelectedCustomer(value);
  };

  const customters = Array.from(
    new Set(["All", ...projects.map((project) => project.customer)]),
  );
  const handleSave = async (row: TableRow, originalRow?: TableRow) => {
    let changedField: string | null = null;

    if (originalRow) {
      if (row.startDate !== originalRow.startDate) {
        changedField = "startDate";
      } else if (row.endDate !== originalRow.endDate) {
        changedField = "endDate";
      } else if (row.isCompleted !== originalRow.isCompleted) {
        changedField = "isCompleted";
      } else if (row.isTracking !== originalRow.isTracking) {
        changedField = "isTracking";
      } else if (row.isTicksheetProject !== originalRow.isTicksheetProject) {
        changedField = "isTicksheetProject";
      } else if (row.division !== originalRow.division) {
        changedField = "division";
      } else if (row.displayName !== originalRow.displayName) {
        changedField = "displayName";
      } else if (row.receivedOn !== originalRow.receivedOn) {
        changedField = "receivedOn";
      }
    }

    // Fallback: if we can't determine which field changed, don't call the mutation
    if (!changedField) {
      console.warn("Could not determine which field was changed");
      return;
    }

    await onCellValueChange(
      {
        ...row,
        startDate: row.startDate as Date,
        endDate: row.endDate as Date,
        receivedOn: row.receivedOn as Date,
      },
      changedField,
    );
  };

  const dataSource: TableData[] = React.useMemo(() => {
    const filteredProjects =
      selectedCustomer && selectedCustomer !== "All"
        ? projects.filter((project) => project.customer === selectedCustomer)
        : projects;

    return filteredProjects.map((project) => {
      return {
        key: project.project,
        ...project,
        startDate: project.startDate ?? null,
        endDate: project.endDate ?? null,
        isCompleted: project.isCompleted ?? false,
        isTracking: project.isTracking ?? false,
        isTicksheetProject: project.isTicksheetProject ?? false,
        division: project.division ?? "",
        receivedOn: project.receivedOn ?? null,
      };
    });
  }, [projects, selectedCustomer]);

  const components = {
    body: {
      row: EditableRow,
      cell: EditableCell,
    },
  };

  const projectsCompleted = projects.filter(
    (p) =>
      (selectedCustomer === "All" && p?.isCompleted) ||
      (p?.customer === selectedCustomer && p?.isCompleted),
  ).length;
  const projectsTracking = projects.filter(
    (p) =>
      (selectedCustomer === "All" && p?.isTracking) ||
      (p?.customer === selectedCustomer && p?.isTracking),
  ).length;
  const projectsTotal = projects.filter(
    (p) => selectedCustomer === "All" || p?.customer === selectedCustomer,
  ).length;

  const columns = COLUMNS.map((col) => {
    if (col.isDate) {
      return {
        ...col,
        render: (_value: any, record: TableRow) => {
          const dateValue = record[col.dataIndex];
          const momentValue = dateValue ? moment(dateValue) : null;
          return (
            <DatePicker
              value={momentValue}
              format="DD-MM-YYYY"
              onChange={(date) => {
                const updatedRecord = {
                  ...record,
                  [col.dataIndex]: date,
                };
                handleSave(updatedRecord, record);
              }}
            />
          );
        },
      };
    }
    if (col.dataIndex === "division") {
      return {
        ...col,
        render: (_value: any, record: TableRow) => {
          const customerProjects = projects.filter(
            (p) => p.customer === record.customer,
          );

          const divisions = Array.from(
            new Set(
              customerProjects
                .map((p) => p.division)
                .filter(
                  (division): division is string =>
                    Boolean(division) && division !== "",
                ),
            ),
          );

          return (
            <DivisionSelector
              value={record[col.dataIndex] ?? ""}
              divisions={divisions}
              onChange={(newDivision) => {
                const updatedRecord = {
                  ...record,
                  division: newDivision,
                };
                handleSave(updatedRecord, record);
              }}
            />
          );
        },
      };
    }
    if (col.isRadio) {
      return {
        ...col,
        render: (_value: any, record: TableRow) => (
          <Switch
            checked={record[col.dataIndex] || false}
            onChange={(checked) => {
              const updatedRecord = {
                ...record,
                [col.dataIndex]: checked,
              };
              handleSave(updatedRecord, record);
            }}
          />
        ),
      };
    }
    if (!col.editable) {
      return col;
    }
    return {
      ...col,
      onCell: (record: TableRow) => ({
        record,
        editable: col.editable,
        dataIndex: col.dataIndex,
        title: col.title,
        handleSave: (updatedRecord: TableRow, originalRecord?: TableRow) =>
          handleSave(updatedRecord, originalRecord),
      }),
    };
  });
  return (
    <Card title="Projects Overview">
      <div>
        <div className={styles["project-table-filter-row"]}>
          <Select
            showSearch
            optionFilterProp="label"
            className={styles.select}
            value={selectedCustomer ?? undefined}
            onChange={handleCustomerChange}
            placeholder="Select a customer"
            options={customters.map((customer) => ({
              label: customer,
              value: customer,
            }))}
          />
          <div className={styles["project-stats-row"]}>
            <span className={styles["project-total-text"]}>
              📋 {projectsTotal} Total
            </span>
            <span className={styles["project-completed-text"]}>
              ✓ {projectsCompleted} Completed
            </span>
            <span className={styles["project-live-text"]}>
              📊 {projectsTracking} Live
            </span>
          </div>
        </div>
      </div>
      <div className={styles["project-button-row"]}>
        <NewProjectButton projects={projects} />
        <EditCustomerButton projects={projects} />
      </div>
      <Table
        key={`table-${projects.length}-${JSON.stringify(
          projects.map((p) => `${p.customer}-${p.project}-${p.division}`),
        )}`}
        dataSource={dataSource}
        components={components}
        columns={columns as ColumnType[]}
        pagination={false}
        scroll={{ x: "max-content" }}
        rowKey={(project) => `${project.customer}-${project.project}`}
      />
    </Card>
  );
};
